/**
 * Data layer for `legacy-mmp-integration-via-api`. Thin typed fetch wrappers over /api/v1/legacy-mmp-integration-via-api.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface LegacyMmpIntegrationViaApiRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListLegacyMmpIntegrationViaApiResponse {
	count: number;
	items: LegacyMmpIntegrationViaApiRow[];
}

export interface CreateLegacyMmpIntegrationViaApiBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/legacy-mmp-integration-via-api";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListLegacyMmpIntegrationViaApiResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListLegacyMmpIntegrationViaApiResponse>(res);
}

export async function create(body: CreateLegacyMmpIntegrationViaApiBody): Promise<LegacyMmpIntegrationViaApiRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<LegacyMmpIntegrationViaApiRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const legacyMmpIntegrationViaApiQueryKeys = {
	all: ["legacy-mmp-integration-via-api"] as const,
	list: () => [...legacyMmpIntegrationViaApiQueryKeys.all] as const,
	detail: (id: string) => [...legacyMmpIntegrationViaApiQueryKeys.all, id] as const,
};
